import { useState, useEffect, useRef } from 'react'
import { ArrowLeft, RefreshCw, Save, AlertCircle, Palette, Check, CheckCircle, Search, Globe, Loader2, MapPin, Settings, Trash2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useMyPlayer, usePlayer, useUpdatePlayer, useCreatePlayer, useDeletePlayer, useGhinLookup } from '../hooks/usePlayers'
import { fetchMyPlayer } from '../api/players'
import { useCourseSearch, useOnlineCourseSearch } from '../hooks/useCourseSearch'
import { hexToRgba, getInitials } from '../utils/colors'
import { formatHandicapIndex, splitHandicapForInput, combineHandicapInput } from '../utils/handicaps'
import PlusHandicapToggle from './shared/PlusHandicapToggle'
import logger from '../utils/logger'
import { extractUserNames } from '../utils/userMeta'
import FollowButton from './FollowButton'
import Spinner from './Spinner'

const AVATAR_COLORS = [
  '#0ea47a', '#5e9be6', '#f59e0b', '#ef4444',
  '#a78bfa', '#f472b6', '#fb923c', '#22d3ee',
]

export default function PlayerProfile({ playerId, onBack, onManageAccount }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { currentTheme, availableThemes, setTheme } = useTheme()
  const { data: myPlayer, isLoading: myLoading } = useMyPlayer()
  const { data: otherPlayer, isLoading: otherLoading } = usePlayer(playerId)
  const updatePlayer = useUpdatePlayer()
  const createPlayer = useCreatePlayer()
  const deletePlayer = useDeletePlayer()
  const ghinLookup = useGhinLookup()

  const isOwnProfile = !playerId
  const player = isOwnProfile ? myPlayer : otherPlayer
  const isLoading = isOwnProfile ? myLoading : otherLoading

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [avatarColor, setAvatarColor] = useState(AVATAR_COLORS[0])
  const [ghinNumber, setGhinNumber] = useState('')
  const [handicapIndex, setHandicapIndex] = useState('')
  const [isPlus, setIsPlus] = useState(false)
  const [homeCourse, setHomeCourse] = useState('')
  const [gender, setGender] = useState('M')
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [autoCreating, setAutoCreating] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const formInitialized = useRef(false)

  // Populate form fields only on initial load (not on every refetch/reconnect).
  // This prevents network-triggered refetches from wiping form state.
  useEffect(() => {
    if (player && !formInitialized.current) {
      formInitialized.current = true
      // Only backfill from auth metadata when viewing own profile
      const { firstName: metaFirst, lastName: metaLast, displayName: metaDisplay } = isOwnProfile ? extractUserNames(user) : {}
      // Backfill first/last/display from user metadata (supports email signup, Google, and Apple OAuth)
      setFirstName(player.first_name || metaFirst || '')
      setLastName(player.last_name || metaLast || '')
      setDisplayName(player.display_name || metaDisplay || '')
      setAvatarColor(player.avatar_color || AVATAR_COLORS[0])
      setGhinNumber(player.ghin_number || '')
      const split = splitHandicapForInput(player.handicap_index)
      setHandicapIndex(split.magnitude)
      setIsPlus(split.isPlus)
      setHomeCourse(player.home_course || '')
      setGender(player.gender || 'M')
      // If we backfilled from metadata, mark as having changes so user can save
      const backfilled = isOwnProfile && ((!player.first_name && metaFirst) ||
        (!player.last_name && metaLast) ||
        (!player.display_name && metaDisplay))
      setHasChanges(!!backfilled)
    }
  }, [player, user])

  // Auto-create player record for new users
  useEffect(() => {
    if (!isOwnProfile || myLoading || myPlayer || !user || autoCreating) return
    setAutoCreating(true)

    // Fresh DB check bypassing React Query cache — guards against race with link_player_by_email()
    fetchMyPlayer().then((existing) => {
      if (existing) {
        // Record already exists (created by link_player_by_email), just refresh the cache
        qc.invalidateQueries({ queryKey: ['my-player'] })
        setAutoCreating(false)
        return
      }
      const { firstName: fn, lastName: ln, displayName: dn } = extractUserNames(user)
      createPlayer.mutate({
        first_name: fn,
        last_name: ln,
        display_name: dn,
        email: user.email,
        avatar_color: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)],
        user_id: user.id,
      }, {
        onSuccess: () => setAutoCreating(false),
        onError: (err) => {
          // 23505 = unique_violation — another process created the record (e.g. link_player_by_email)
          if (err?.code === '23505') {
            qc.invalidateQueries({ queryKey: ['my-player'] })
          }
          setAutoCreating(false)
        },
      })
    }).catch(() => {
      setAutoCreating(false)
    })
  }, [isOwnProfile, myLoading, myPlayer, user, autoCreating])

  const markChanged = () => {
    setHasChanges(true)
    setSaveSuccess(false)
    setSaveError(null)
  }

  // Auto-fill display name from first+last if user hasn't customized it
  const autoFillDisplayName = (newFirst, newLast) => {
    const prevFullName = [player?.first_name, player?.last_name].filter(Boolean).join(' ')
    const currentIsDefault = !displayName || displayName === prevFullName ||
      displayName === `${firstName} ${lastName}`.trim()
    if (currentIsDefault && newFirst.trim() && newLast.trim()) {
      setDisplayName(`${newFirst.trim()} ${newLast.trim()}`)
    }
  }

  const handleSave = () => {
    setSaveError(null)
    // For creator-edited manual players, only display_name is required (no first/last requirement)
    if (!isCreatorOfUnlinked && (!firstName.trim() || !lastName.trim())) {
      setSaveError('First and last name are required.')
      return
    }
    let finalDisplayName = displayName.trim()
    if (firstName.trim() && lastName.trim()) {
      const newFullName = `${firstName.trim()} ${lastName.trim()}`
      const prevFullName = player ? [player.first_name, player.last_name].filter(Boolean).join(' ') : ''
      if (!finalDisplayName || finalDisplayName === prevFullName) {
        finalDisplayName = newFullName
      }
    }
    finalDisplayName = finalDisplayName || player?.display_name || user?.email?.split('@')[0] || 'Player'

    const saveCallbacks = {
      onSuccess: () => {
        setHasChanges(false)
        setSaveSuccess(true)
      },
      onError: (err) => {
        logger.capture(err, { source: 'PlayerProfile', action: 'save' })
        setSaveError(err.message || 'Failed to save profile')
      },
    }

    const handicapValue = combineHandicapInput({ isPlus, magnitude: handicapIndex })

    // If no player record exists yet, create one instead of updating
    if (!player) {
      createPlayer.mutate({
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        display_name: finalDisplayName,
        email: user.email,
        avatar_color: avatarColor,
        user_id: user.id,
        ghin_number: ghinNumber.trim() || null,
        handicap_index: handicapValue ?? 0,
        home_course: homeCourse.trim() || null,
        gender,
      }, saveCallbacks)
      return
    }

    const hcpChanged = handicapValue != null && handicapValue !== Number(player.handicap_index)
    const updates = {
      id: player.id,
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
      display_name: finalDisplayName,
      avatar_color: avatarColor,
      ghin_number: ghinNumber.trim() || null,
      handicap_index: handicapValue != null ? handicapValue : player.handicap_index,
      home_course: homeCourse.trim() || null,
      gender,
      ...(hcpChanged && ghinNumber.trim() ? { handicap_updated_at: new Date().toISOString() } : {}),
    }
    updatePlayer.mutate(updates, saveCallbacks)
  }

  const handleGhinLookup = () => {
    if (!ghinNumber.trim()) return
    ghinLookup.mutate(ghinNumber.trim(), {
      onSuccess: (data) => {
        if (data.handicap_index != null) {
          const split = splitHandicapForInput(data.handicap_index)
          setHandicapIndex(split.magnitude)
          setIsPlus(split.isPlus)
          markChanged()
        }
        if (data.gender) {
          setGender(data.gender)
          markChanged()
        }
      },
    })
  }

  const handleDelete = () => {
    deletePlayer.mutate(player.id, {
      onSuccess: onBack,
      onError: (err) => {
        logger.capture(err, { source: 'PlayerProfile', action: 'delete' })
        setShowDeleteConfirm(false)
      },
    })
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spinner message="Loading profile..." />
      </div>
    )
  }

  // Editable if own profile OR if current user created this unlinked player.
  // Cross-reference the follows cache as a fallback: usePlayer() has no staleTime
  // so it always refetches on mount, and if the fetch is delayed or fails the
  // follows cache (already warm from FollowsList) has the same data — it's what
  // powers the Edit button that brought the user here in the first place.
  const followsCache = qc.getQueryData(['my-follows'])
  const followsEntry = Array.isArray(followsCache) ? followsCache.find((p) => p.id === playerId) : null
  const isCreatorOfUnlinked = !isOwnProfile && (
    (player && player.created_by === user?.id && !player.user_id) ||
    (followsEntry && followsEntry.created_by === user?.id && !followsEntry.user_id)
  )
  const readOnly = !isOwnProfile && !isCreatorOfUnlinked

  // Build list of missing fields for own profile or creator-edited profiles
  const missingFields = []
  if (isOwnProfile) {
    if (!player?.first_name || !player?.last_name) missingFields.push('Name')
    if (player?.handicap_index == null || Number(player?.handicap_index) === 0) missingFields.push('Handicap Index')
  } else if (isCreatorOfUnlinked) {
    if (player?.handicap_index == null || Number(player?.handicap_index) === 0) missingFields.push('Handicap Index')
  }

  // Build initials from current form state (reactive to edits)
  const initials = (firstName && lastName)
    ? (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
    : getInitials(player) || (displayName || '?').charAt(0).toUpperCase()
  const color = avatarColor || player?.avatar_color

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={onBack}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-tertiary)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>
          {isOwnProfile ? 'My Profile' : isCreatorOfUnlinked ? 'Edit Player' : (player?.display_name || 'Player Profile')}
        </h1>
      </div>

      {/* Incomplete profile banner */}
      {missingFields.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.625rem',
          padding: '0.75rem 1rem',
          marginBottom: '1.25rem',
          borderRadius: '0.625rem',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.25)',
        }}>
          <AlertCircle size={18} style={{ color: '#f59e0b', flexShrink: 0, marginTop: '1px' }} />
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Complete your profile</span>
            <br />
            Add your {missingFields.join(' and ')} for accurate handicap scoring.
          </div>
        </div>
      )}

      {/* Avatar section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '1.5rem',
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '700',
          fontSize: initials.length > 1 ? '1.5rem' : '2rem',
          color: '#fff',
          marginBottom: '0.75rem',
        }}>
          {initials}
        </div>

        {/* Color picker (own profile only) */}
        {!readOnly && (
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            {AVATAR_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => { setAvatarColor(c); markChanged() }}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: c,
                  border: avatarColor === c ? '3px solid var(--text-primary)' : '2px solid transparent',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s',
                }}
              />
            ))}
          </div>
        )}

        {/* Follow button (other registered players only — not manual/unlinked) */}
        {readOnly && player && player.user_id && (
          <FollowButton playerId={player.id} />
        )}
      </div>

      {/* Profile fields card */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}>
        {/* First Name / Last Name — side by side */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>First Name{!player?.first_name && !readOnly && <span style={{ color: '#ef4444' }}> *</span>}</label>
            {readOnly ? (
              <div style={readOnlyStyle}>{player?.first_name || '—'}</div>
            ) : (
              <input
                type="text"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); autoFillDisplayName(e.target.value, lastName); markChanged() }}
                style={inputStyle}
                placeholder="First"
              />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Last Name{!player?.last_name && !readOnly && <span style={{ color: '#ef4444' }}> *</span>}</label>
            {readOnly ? (
              <div style={readOnlyStyle}>{player?.last_name || '—'}</div>
            ) : (
              <input
                type="text"
                value={lastName}
                onChange={(e) => { setLastName(e.target.value); autoFillDisplayName(firstName, e.target.value); markChanged() }}
                style={inputStyle}
                placeholder="Last"
              />
            )}
          </div>
        </div>

        {/* Display Name */}
        <div>
          <label style={labelStyle}>Display Name</label>
          {readOnly ? (
            <div style={readOnlyStyle}>{player?.display_name || '—'}</div>
          ) : (
            <input
              type="text"
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); markChanged() }}
              style={inputStyle}
              placeholder="Your display name"
            />
          )}
        </div>

        {/* Email */}
        <div>
          <label style={labelStyle}>Email</label>
          <div style={readOnlyStyle}>{player?.email || (isOwnProfile ? user?.email : null) || '—'}</div>
        </div>

        {/* GHIN Number */}
        <div>
          <label style={labelStyle}>GHIN Number</label>
          {readOnly ? (
            <div style={readOnlyStyle}>{player?.ghin_number || '—'}</div>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={ghinNumber}
                onChange={(e) => { setGhinNumber(e.target.value); markChanged() }}
                style={{ ...inputStyle, flex: 1 }}
                placeholder="e.g. 1234567"
              />
              <button
                onClick={handleGhinLookup}
                disabled={ghinLookup.isPending || !ghinNumber.trim()}
                style={{
                  height: '44px',
                  padding: '0 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.8125rem',
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: 'var(--accent-primary)',
                  border: 'none',
                  cursor: ghinLookup.isPending || !ghinNumber.trim() ? 'not-allowed' : 'pointer',
                  opacity: ghinLookup.isPending || !ghinNumber.trim() ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  flexShrink: 0,
                }}
              >
                <RefreshCw size={14} style={ghinLookup.isPending ? { animation: 'spin 1s linear infinite' } : {}} />
                Lookup
              </button>
            </div>
          )}
          {ghinLookup.isError && (
            <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.375rem' }}>
              {ghinLookup.error?.message || 'GHIN lookup failed'}
            </div>
          )}
          {ghinLookup.isSuccess && !ghinLookup.isError && (
            <div style={{ color: 'var(--accent-primary)', fontSize: '0.75rem', marginTop: '0.375rem' }}>
              Handicap updated from GHIN
            </div>
          )}
        </div>

        {/* Handicap Index */}
        <div>
          <label style={labelStyle}>Handicap Index{(player?.handicap_index == null || Number(player?.handicap_index) === 0) && !readOnly && <span style={{ color: '#ef4444' }}> *</span>}</label>
          {readOnly ? (
            <div style={readOnlyStyle}>
              {player?.handicap_index != null ? formatHandicapIndex(player.handicap_index) : '—'}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <PlusHandicapToggle
                  isPlus={isPlus}
                  onChange={(next) => { setIsPlus(next); markChanged() }}
                />
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.1"
                  value={handicapIndex}
                  onChange={(e) => { setHandicapIndex(e.target.value); markChanged() }}
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="e.g. 12.4"
                />
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>
                Tap <strong>+ Plus</strong> for handicaps better than scratch (e.g. +1.4)
              </p>
            </>
          )}
        </div>

        {/* Home Course */}
        <div>
          <label style={labelStyle}>Home Course</label>
          {readOnly ? (
            <div style={readOnlyStyle}>{player?.home_course || '—'}</div>
          ) : (
            <HomeCourseSearch
              value={homeCourse}
              onChange={(name) => { setHomeCourse(name); markChanged() }}
            />
          )}
        </div>

        {/* Gender */}
        <div>
          <label style={labelStyle}>Gender</label>
          {readOnly ? (
            <div style={readOnlyStyle}>{player?.gender === 'F' ? 'Female' : 'Male'}</div>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setGender(value); markChanged() }}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    borderRadius: '0.5rem',
                    border: `1px solid ${gender === value ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    backgroundColor: gender === value ? 'var(--accent-primary)' : 'var(--bg-primary)',
                    color: gender === value ? '#fff' : 'var(--text-primary)',
                    fontWeight: gender === value ? 600 : 400,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Theme picker (own profile only) */}
      {isOwnProfile && (
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '0.75rem',
          border: '1px solid var(--border-color)',
          padding: '1.25rem',
          marginTop: '1rem',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.75rem',
          }}>
            <Palette size={16} style={{ color: 'var(--text-muted)' }} />
            <label style={{ ...labelStyle, marginBottom: 0 }}>Theme</label>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {availableThemes.map((t) => (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: currentTheme === t.id ? '600' : '500',
                  color: currentTheme === t.id ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  backgroundColor: currentTheme === t.id ? 'var(--bg-tertiary)' : 'transparent',
                  border: currentTheme === t.id ? '1px solid var(--accent-primary)' : '1px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {currentTheme === t.id && <Check size={14} />}
                {t.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Manage Account link (own profile only) */}
      {isOwnProfile && (
        <button
          onClick={onManageAccount}
          style={{
            width: '100%',
            marginTop: '2rem',
            padding: '0.75rem',
            borderRadius: '9999px',
            fontSize: '0.8125rem',
            fontWeight: '500',
            color: 'var(--text-muted)',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
          }}
        >
          <Settings size={14} />
          Manage Account
        </button>
      )}

      {/* Save button */}
      {!readOnly && (hasChanges || !player) && (
        <button
          onClick={handleSave}
          disabled={updatePlayer.isPending || createPlayer.isPending}
          style={{
            width: '100%',
            height: '44px',
            marginTop: '1.25rem',
            borderRadius: '9999px',
            fontSize: '0.9375rem',
            fontWeight: '700',
            color: '#fff',
            backgroundColor: 'var(--accent-primary)',
            border: 'none',
            cursor: (updatePlayer.isPending || createPlayer.isPending) ? 'wait' : 'pointer',
            opacity: (updatePlayer.isPending || createPlayer.isPending) ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
        >
          <Save size={16} />
          {(updatePlayer.isPending || createPlayer.isPending) ? 'Saving...' : 'Save Changes'}
        </button>
      )}

      {saveError && (
        <div style={{
          textAlign: 'center',
          color: '#ef4444',
          fontSize: '0.8125rem',
          fontWeight: '600',
          marginTop: '0.75rem',
        }}>
          {saveError}
        </div>
      )}

      {/* Delete button — creator-only, unlinked players */}
      {isCreatorOfUnlinked && (
        <button
          onClick={() => setShowDeleteConfirm(true)}
          style={{
            width: '100%',
            height: '44px',
            marginTop: '1.5rem',
            borderRadius: '9999px',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: 'var(--accent-danger, #ef4444)',
            backgroundColor: 'transparent',
            border: '1.5px solid rgba(239, 68, 68, 0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
          }}
        >
          <Trash2 size={15} />
          Delete Player
        </button>
      )}

      {saveSuccess && (
        <div className="modal-overlay" onClick={onBack}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-content">
              <div className="success-icon">
                <CheckCircle size={48} />
              </div>
              <p>Profile saved!</p>
            </div>
            <div className="modal-actions">
              <button className="btn-primary btn-action" onClick={onBack}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-content">
              <div className="warning-icon danger">
                <Trash2 size={48} />
              </div>
              <p>Delete {player?.display_name}?</p>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                This cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletePlayer.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDelete}
                disabled={deletePlayer.isPending}
              >
                {deletePlayer.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spin animation for GHIN lookup */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.375rem',
}

const inputStyle = {
  width: '100%',
  height: '44px',
  padding: '0 0.75rem',
  borderRadius: '0.5rem',
  fontSize: '0.9375rem',
  fontWeight: '500',
  backgroundColor: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
  outline: 'none',
  boxSizing: 'border-box',
}

const readOnlyStyle = {
  fontSize: '0.9375rem',
  fontWeight: '500',
  color: 'var(--text-secondary)',
  padding: '0.5rem 0',
}

function HomeCourseSearch({ value, onChange }) {
  const [query, setQuery] = useState(value || '')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [searchOnline, setSearchOnline] = useState(false)
  const [selected, setSelected] = useState(!!value)
  const containerRef = useRef(null)

  useEffect(() => {
    setQuery(value || '')
    setSelected(!!value)
  }, [value])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const { data: dbResults, isLoading: dbLoading } = useCourseSearch(debouncedQuery)
  const { data: onlineResults, isLoading: onlineLoading } = useOnlineCourseSearch(debouncedQuery, searchOnline)

  const results = dbResults || []
  const onlineList = onlineResults || []
  const isLoading = dbLoading || onlineLoading
  const hasResults = results.length > 0 || onlineList.length > 0
  const showOnlineOption = debouncedQuery.length >= 3 && results.length < 5 && !searchOnline

  const handleSelect = async (result, isOnline) => {
    const name = result.short_name || result.course_name
    setQuery(name)
    setSelected(true)
    setShowResults(false)
    setSearchOnline(false)
    if (isOnline) {
      try {
        const { fetchCourseOnline } = await import('../api/courses')
        await fetchCourseOnline(result.id)
      } catch { /* best effort */ }
    }
    onChange(name)
  }

  const handleClear = () => {
    setQuery('')
    setSelected(false)
    setSearchOnline(false)
    onChange('')
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={16} style={{
          position: 'absolute',
          left: '0.75rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-muted)',
          pointerEvents: 'none',
        }} />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setSelected(false)
            setShowResults(true)
            setSearchOnline(false)
            if (!e.target.value) handleClear()
          }}
          onFocus={() => {
            if (!selected && query.length >= 3) setShowResults(true)
          }}
          placeholder="Search for a golf course..."
          style={{
            ...inputStyle,
            paddingLeft: '2.25rem',
            paddingRight: selected ? '2rem' : undefined,
          }}
        />
        {selected && query && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              fontSize: '1.25rem',
              lineHeight: 1,
              padding: '0.25rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        )}
        {isLoading && !selected && (
          <Loader2 size={16} style={{
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            animation: 'spin 1s linear infinite',
          }} />
        )}
      </div>

      {showResults && debouncedQuery.length >= 3 && !selected && (
        <div style={{
          position: 'absolute',
          zIndex: 20,
          left: 0,
          right: 0,
          marginTop: '0.25rem',
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '0.5rem',
          maxHeight: '220px',
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}>
          {results.map((r) => (
            <button
              key={r.id}
              onClick={() => handleSelect(r, false)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.125rem',
                width: '100%',
                padding: '0.625rem 0.75rem',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                borderBlockEnd: '1px solid var(--border-color)',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                {r.short_name || r.course_name}
              </span>
              {r.club_name && r.club_name !== r.course_name && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.club_name}</span>
              )}
              {(r.city || r.state) && (
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <MapPin size={10} />
                  {[r.city, r.state].filter(Boolean).join(', ')}
                </span>
              )}
            </button>
          ))}

          {searchOnline && onlineList.map((r) => (
            <button
              key={`online-${r.id}`}
              onClick={() => handleSelect(r, true)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.125rem',
                width: '100%',
                padding: '0.625rem 0.75rem',
                textAlign: 'left',
                background: 'rgba(var(--accent-secondary-rgb, 245, 158, 11), 0.03)',
                border: 'none',
                borderBlockEnd: '1px solid var(--border-color)',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <Globe size={12} style={{ color: 'var(--accent-secondary, #f59e0b)', flexShrink: 0 }} />
                <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  {r.course_name}
                </span>
              </div>
              {(r.city || r.state) && (
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', paddingLeft: '1.25rem' }}>
                  {[r.city, r.state].filter(Boolean).join(', ')}
                </span>
              )}
            </button>
          ))}

          {!isLoading && !hasResults && (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              No courses found
            </div>
          )}

          {showOnlineOption && (
            <button
              onClick={() => setSearchOnline(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                width: '100%',
                padding: '0.625rem',
                color: 'var(--accent-secondary, #f59e0b)',
                fontSize: '0.8125rem',
                fontWeight: '600',
                background: 'none',
                border: 'none',
                borderTop: results.length > 0 ? '1px solid var(--border-color)' : 'none',
                cursor: 'pointer',
              }}
            >
              <Globe size={14} />
              Search online for more courses
            </button>
          )}
        </div>
      )}
    </div>
  )
}
